import { useEffect } from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions'
import { Markdown, type MarkdownStorage } from 'tiptap-markdown'
import { Highlight } from '@tiptap/extension-highlight'
import {
  Bold,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  List,
  ListOrdered,
  Underline,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Reusable WYSIWYG editor (TipTap) whose external value is markdown by default
 * and HTML when `format="html"`.
 *
 * The markdown (de)serialisation runs through `tiptap-markdown`, so callers
 * keep talking plain markdown (e.g. the `body_md` API contract of the mail
 * templates) while users edit rich text. Template placeholders such as
 * `{{ startup_name }}` are ordinary text inside the editor; the serializer's
 * character escaping is undone within `{{ … }}` spans so placeholders land
 * verbatim in the markdown output (see `unescapePlaceholders`).
 *
 * The HTML mode exists for the spec-58 note bodies, whose storage form is the
 * sanitised HTML of `backend/app/preinvest/rich_text.py`. Highlighting is only
 * offered there: `tiptap-markdown` would serialise a highlight as `==text==`,
 * which would land verbatim in a mail.
 */

/**
 * prosemirror-markdown escapes markdown-significant characters in text nodes
 * (e.g. `founder_name` → `founder\_name` in some positions). Inside a
 * `{{ … }}` placeholder that escaping would break the backend's variable
 * substitution, so undo it there. Outside placeholders escaping is preserved.
 */
function unescapePlaceholders(markdown: string): string {
  return markdown.replace(/\{\{[^{}\n]*\}\}/g, (span) =>
    span.replace(/\\([\\`*_~[\]<>#!(){}+.-])/g, '$1'),
  )
}

function getMarkdown(editor: Editor): string {
  // tiptap-markdown registers its storage under the `markdown` key but does
  // not augment TipTap's Storage interface, hence the cast.
  const storage = editor.storage as unknown as { markdown: MarkdownStorage }
  return unescapePlaceholders(storage.markdown.getMarkdown())
}

export type RichTextFormat = 'markdown' | 'html'

/**
 * The editor's external value. Markdown keeps the existing `body_md` contract
 * of the mail templates and the IC pre-read; HTML is the storage form of the
 * spec-58 note bodies, where the whitelist (and `<mark>`) live in HTML.
 */
function getValue(editor: Editor, format: RichTextFormat): string {
  return format === 'html' ? editor.getHTML() : getMarkdown(editor)
}

/**
 * The document text before the caret, with block breaks as newlines. This is
 * what a textarea gave callers for free through `selectionStart`, and what the
 * note composer needs to spot an `@mention` being typed.
 */
function textUpToCaret(editor: Editor): string {
  return editor.state.doc.textBetween(0, editor.state.selection.from, '\n', ' ')
}

export interface RichTextHandle {
  /** The TipTap instance, for commands this component does not wrap. */
  editor: Editor
  /**
   * Replace the `length` characters before the caret with `text`. Positions in
   * a text block advance one per character, so a caller that counted
   * characters in `textUpToCaret` can pass that count straight in.
   */
  replaceBeforeCaret: (length: number, text: string) => void
}

function ToolbarButton({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      // Keep focus (and thus the selection) inside the editor while clicking.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50',
        active && 'bg-muted text-foreground',
      )}
    >
      {children}
    </button>
  )
}

export function RichTextEditor({
  id,
  value,
  onChange,
  disabled = false,
  placeholder,
  ariaLabel,
  ariaDescribedBy,
  className,
  format = 'markdown',
  onReady,
}: {
  /** Optional id for the contenteditable, so a `<Label htmlFor>` can point at it. */
  id?: string
  /** Markdown source, or HTML when `format` is `'html'`. */
  value: string
  /**
   * Called with the updated value in `format` on every edit. The second
   * argument is the plain text before the caret; callers that do not track a
   * caret can ignore it.
   */
  onChange: (value: string, textUpToCaret: string) => void
  disabled?: boolean
  placeholder?: string
  /**
   * Accessible name for the textbox. A `<label htmlFor>` does not associate
   * with a contenteditable div (not a labelable element), so pass the name
   * explicitly.
   */
  ariaLabel?: string
  /** Id of the element describing the textbox (e.g. a live word counter). */
  ariaDescribedBy?: string
  className?: string
  /**
   * Value format. Defaults to markdown, which is what the mail templates, the
   * investment thesis and the IC pre-read store.
   */
  format?: RichTextFormat
  /**
   * Imperative handle, as soon as the editor exists. The note composer needs
   * it to insert a picked @mention over the typed token, which is not
   * expressible through `value`/`onChange`.
   */
  onReady?: (handle: RichTextHandle) => void
}) {
  const html = format === 'html'
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Mail bodies render inside the branded mail wrapper: h1 is reserved
        // for the mail header there, so offer h2/h3 only.
        heading: { levels: [2, 3] },
        // No inline links UI yet; autolinking plain URLs is enough for mails.
        link: { openOnClick: false },
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      // Only in HTML mode: `tiptap-markdown` would serialise a highlight as
      // `==text==`, which would land verbatim in a mail template's body_md.
      ...(html
        ? [Highlight]
        : [
            Markdown.configure({
              html: false,
              tightLists: true,
              transformPastedText: true,
              transformCopiedText: true,
            }),
          ]),
    ],
    content: value,
    editable: !disabled,
    // Small editor: rerendering per transaction is cheap and keeps the
    // toolbar's active-state in sync without a selector subscription.
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
        role: 'textbox',
        'aria-multiline': 'true',
        // vonk-prose styles the elements (headings, lists, marks); the editor
        // class keeps the editor's own box, placeholder and outline.
        class: 'vonk-rte__prosemirror vonk-prose',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(getValue(editor, format), textUpToCaret(editor))
    },
  })

  // Sync external value changes (e.g. a form reset) into the editor without
  // clobbering the cursor while the user is the source of the change.
  useEffect(() => {
    if (!editor) return
    if (getValue(editor, format) !== value) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value, format])

  useEffect(() => {
    if (!editor) return
    onReady?.({
      editor,
      replaceBeforeCaret: (length, text) => {
        const to = editor.state.selection.from
        editor
          .chain()
          .focus()
          .deleteRange({ from: to - length, to })
          .insertContent(text)
          .run()
      },
    })
  }, [editor, onReady])

  // `setEditable` emits an `update` event unless told not to, and that update is
  // indistinguishable from a real edit: this effect runs on mount, so every
  // editor reported a change to its parent before the user had touched it. In
  // the IC pre-read that marked the card dirty on load, which disables its
  // "Exporteer PDF" button for as long as the card is open.
  useEffect(() => {
    if (!editor) return
    editor.setEditable(!disabled, false)
  }, [editor, disabled])

  const toolbarDisabled = disabled || !editor

  return (
    <div className={cn('vonk-rte', disabled && 'vonk-rte--disabled', className)}>
      <div
        role="toolbar"
        aria-label="Opmaak"
        className="flex flex-wrap items-center gap-0.5 border-b border-border p-1"
      >
        <ToolbarButton
          label="Vet"
          active={editor?.isActive('bold') ?? false}
          disabled={toolbarDisabled}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Cursief"
          active={editor?.isActive('italic') ?? false}
          disabled={toolbarDisabled}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-4" />
        </ToolbarButton>
        {html ? (
          <>
            <ToolbarButton
              label="Onderstrepen"
              active={editor?.isActive('underline') ?? false}
              disabled={toolbarDisabled}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
            >
              <Underline className="size-4" />
            </ToolbarButton>
            <ToolbarButton
              label="Markeren"
              active={editor?.isActive('highlight') ?? false}
              disabled={toolbarDisabled}
              onClick={() => editor?.chain().focus().toggleHighlight().run()}
            >
              <Highlighter className="size-4" />
            </ToolbarButton>
          </>
        ) : null}
        <div aria-hidden className="mx-1 h-5 w-px bg-border" />
        <ToolbarButton
          label="Kop"
          active={editor?.isActive('heading', { level: 2 }) ?? false}
          disabled={toolbarDisabled}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Subkop"
          active={editor?.isActive('heading', { level: 3 }) ?? false}
          disabled={toolbarDisabled}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="size-4" />
        </ToolbarButton>
        <div aria-hidden className="mx-1 h-5 w-px bg-border" />
        <ToolbarButton
          label="Opsomming"
          active={editor?.isActive('bulletList') ?? false}
          disabled={toolbarDisabled}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Genummerde lijst"
          active={editor?.isActive('orderedList') ?? false}
          disabled={toolbarDisabled}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-4" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} className="vonk-rte__content" />
    </div>
  )
}
